import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Chip, Typography } from '@mui/material';
import _ from 'lodash';
import { RHFMultipleSelectField } from '../../common/hook-form';
import { placed, transit, delivered, deliveryFailed, rto, lost, cancelled } from '../../common/util';

// Every order status a PTL shipment list can be filtered by.
export const ALL_ORDER_STATUSES = [...placed, ...transit, ...delivered, ...deliveryFailed, ...rto, ...lost, ...cancelled];

// "All" option: RHFMultipleSelectField ticks every status for it and stores ['ALL'].
const ALL = 'ALL';

/**
 * "Order Status" multi-select for the PTL shipment lists (form field `status`, value: array).
 * Kept outside the page components so a page re-render doesn't re-create it and close the menu mid-selection.
 */
export default function OrderStatusFilter({ statuses = ALL_ORDER_STATUSES }) {
    const options = useMemo(
        () => [{ label: 'All', value: ALL }, ..._.orderBy(statuses.map((op) => ({ label: _.startCase(op), value: op })), 'value')],
        [statuses]
    );
    return (
        <RHFMultipleSelectField name="status" variant="outlined" width="100%" label="Order Status"
            placeholder="Select Order Status" options={options} search outputFormat="array" />
    );
}

/**
 * Chips for the APPLIED statuses; the × on one removes it and re-applies the filter.
 * Render inside the page's FormProvider; `onApply` is the page's filter submit handler.
 */
export function OrderStatusChips({ applied, onApply }) {
    const { setValue, handleSubmit } = useFormContext();
    const list = toStatusList(applied);
    if (!list.length) return null;
    const remove = (status) => {
        setValue('status', list.filter((s) => s !== status));
        handleSubmit(onApply)();
    };
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Order Status:</Typography>
            {list.map((s) => <Chip key={s} size="small" label={s === ALL ? 'All statuses' : _.startCase(s)} onDelete={() => remove(s)} />)}
        </Box>
    );
}

const toStatusList = (status) => (Array.isArray(status) ? status : [status]).filter(Boolean);

/**
 * andfilter clause for the selected statuses (array, or a single value), or null when none is selected.
 * "All" also returns null: every status is wanted, so the list is not restricted by status at all.
 */
export const orderStatusClause = (status) => {
    const list = toStatusList(status);
    return list.length && !list.includes(ALL) ? { field: 'orderStatus', operator: 'in', value: list } : null;
};
